import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { MainePhone } from './MainePhone';
import { Email } from './Email';
import { BackupPhones } from './BackupPhones';
import { Phone } from '../../../models/UserInfo';
import { SocialsSetting } from './SocialsSetting';
import { TerminateContract } from '../../forms/TerminateContract';
import { TextButton, TextButtonClassNames } from '../../commons/buttons/TextButton';
import { ITerminationStep } from '../../../models/ContractTermination';
import { GlobalRouteNames, ModalVariants } from '../../../models/Common';
import { useDateLocalization } from '../../../hooks/useDateLocalization';
import { modalSlice } from '../../../store/reducers/ModalSlice';
import { useUserAddress } from '../../../hooks/useUserAddress';
import { useCustomSnackbar } from '../../../hooks/useCustomEnqueueSnackbar';
import { userInfoApi } from '../../../services/apiServices/user/userInfo';
import { LOG_ROCKET_LAUNCH_ID } from '../../../constants/configuration';
import { ReactComponent as ArrowDropdown } from '../../../assets/images/arrows/ArrowDefault.svg';
import { getErrorMessage } from '../../../utils';

interface UserInfoItems {
  label: string;
  value: string | number | Phone[] | null;
}

export const Account: FC = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const history = useHistory();
  const { formatDate } = useDateLocalization();
  const [terminateStep, setTerminateStep] = useState<ITerminationStep>();
  const { userInfoState, isUserInfoDownloaded } = useAppSelector((state) => state.userInfoReducer);
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;
  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const { userAddress } = useUserAddress(userInfoState, selectedLanguage);
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [resendEmailApi, { isLoading: isResendingEmail }] = userInfoApi.useResendEmailMutation();

  useEffect(() => {
    if (isUserInfoDownloaded) {
      const searchParams = new URLSearchParams(search);
      const isTermination = searchParams.has('terminate-agreement');
      if (isTermination && userInfoState.TypeID === 2) {
        history.replace(GlobalRouteNames.AccessDenied);
        return;
      }
      setTerminateStep(isTermination ? 'run' : undefined);
      window.scrollTo(0, 0);
    }
  }, [search, isUserInfoDownloaded]);

  const handleEditEmail = () => {
    dispatch(openModal({ modalVariants: ModalVariants.EditEmail }));
  };

  const handleAddNewEmail = () => {
    dispatch(openModal({ modalVariants: ModalVariants.AddNewEmail }));
  };

  const handleDeleteEmail = () => {
    if (isUserInfoDownloaded) {
      dispatch(
        openModal({
          modalVariants: ModalVariants.DeleteEmail,
          content: { email: userInfoState.EmailSettings.Email },
        }),
      );
    }
  };

  const handleResendEmail = (email: string) => {
    return async () => {
      try {
        await resendEmailApi()
          .unwrap()
          .then(() => {
            dispatch(
              openModal({
                modalVariants: ModalVariants.Success,
                content: {
                  title: t('common.done'),
                  description: `${t('accountSettings.confirmationMessageHasBeenSentToMail')} - `,
                  boldDescription: email,
                },
              }),
            );
          });
      } catch (err) {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      }
    };
  };

  const handleChangeBackupPhone = (phone: string, phoneId: number) => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.EditBackupPhone,
        content: { phone, phoneId },
      }),
    );
  };

  const handleAddNewBackupPhone = () => {
    dispatch(openModal({ modalVariants: ModalVariants.AddNewBackupPhone }));
  };

  const handleDeleteBackupPhone = (phone: string, phoneId: number) => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.DeleteBackupPhone,
        content: { phone, phoneId },
      }),
    );
  };

  const handleAddSmsPhone = (title: string, description?: string) => {
    dispatch(openModal({ modalVariants: ModalVariants.AddNewSmsPhone, content: { title, description } }));
  };

  const handleEditSmsPhone = (title: string, description?: string) => {
    dispatch(openModal({ modalVariants: ModalVariants.EditSmsPhone, content: { title, description } }));
  };

  const handleDeleteSmsPhone = (phone: string) => {
    dispatch(openModal({ modalVariants: ModalVariants.DeleteSmsPhone, content: { phone } }));
  };

  const userInfoItems: UserInfoItems[] = [
    { label: t('accountSettings.fullName'), value: userInfoState.Name },
    {
      label: t('accountSettings.Address'),
      value: userAddress,
    },
    { label: t('accountSettings.mainPhoneNumber'), value: userInfoState.SmsSettings?.Phone },
    {
      label: t('accountSettings.connectionDate'),
      value: formatDate(userInfoState.CreateDate, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        locale: 'uk',
      }),
    },
    {
      label: t('userInfo.backupPhoneNumbers'),
      value: userInfoState.Phones.length ? userInfoState.Phones : null,
    },
    { label: t('userInfo.email'), value: userInfoState.EmailSettings?.Email || null },
  ];

  const getLabelValue = ({ label, value }: UserInfoItems) => {
    const formattedValue = Array.isArray(value) ? '' : value;
    const formattedString = String(formattedValue);

    switch (label) {
      case t('accountSettings.mainPhoneNumber'): {
        return (
          <MainePhone
            value={formattedValue}
            handleAddPhone={handleAddSmsPhone}
            handleDeletePhone={handleDeleteSmsPhone}
            handleEditSmsPhone={handleEditSmsPhone}
          />
        );
      }
      case t('userInfo.email'): {
        return (
          <Email
            handleAddNewEmail={handleAddNewEmail}
            handleEditEmail={handleEditEmail}
            handleDeleteEmail={handleDeleteEmail}
            handleResendEmail={handleResendEmail(formattedString)}
            value={formattedValue}
            isResendDisabled={isResendingEmail}
            awaitingConfirm={
              isUserInfoDownloaded && userInfoState.EmailSettings !== null && !userInfoState.EmailSettings.IsConfirmed
            }
          />
        );
      }
      case t('userInfo.backupPhoneNumbers'): {
        return (
          <BackupPhones
            handleAddPhone={handleAddNewBackupPhone}
            changePhone={handleChangeBackupPhone}
            deletePhone={handleDeleteBackupPhone}
            value={value}
          />
        );
      }
      default:
        return value;
    }
  };

  const handleLaunchTermination = () => {
    history.push(`${GlobalRouteNames.AccountSettingPage}?terminate-agreement`);
  };

  const handleChangeTerminateStep = (step: ITerminationStep) => {
    if (!step) {
      history.push(GlobalRouteNames.AccountSettingPage);
      return;
    }
    setTerminateStep(step);
    window.scrollTo(0, 0);
  };

  return (
    <div className="account">
      {terminateStep ? (
        <TerminateContract step={terminateStep} setStep={handleChangeTerminateStep} />
      ) : (
        <>
          <div className="account__info">
            {userInfoItems.map((el) => {
              return (
                <div
                  className={
                    el.label === t('userInfo.backupPhoneNumbers')
                      ? 'account__info__item account__info__item__phones'
                      : 'account__info__item'
                  }
                  key={el.label}
                >
                  <div className="account__info__item__label">
                    <p
                      className="account__info__item__label__title"
                      id={el.label === t('accountSettings.fullName') ? LOG_ROCKET_LAUNCH_ID : undefined}
                    >
                      {el.label}
                    </p>
                    {el.label === t('userInfo.email') &&
                    isUserInfoDownloaded &&
                    userInfoState.EmailSettings !== null &&
                    !userInfoState.EmailSettings.IsConfirmed ? (
                      <p className="account__info__item__label__email-verification">{t('userInfo.emailIsConfirmed')}</p>
                    ) : null}
                  </div>
                  <div
                    className={
                      el.label === t('userInfo.backupPhoneNumbers')
                        ? 'account__info__item__description account__info__item__phones__description'
                        : 'account__info__item__description'
                    }
                  >
                    {getLabelValue({ label: el.label, value: el.value })}
                  </div>
                </div>
              );
            })}
          </div>
          <hr />
          <SocialsSetting />
          {isUserInfoDownloaded && userInfoState.TypeID !== 2 && (
            <div className="account__terminate">
              <TextButton
                label={t('terminateContract.launch')}
                handleClick={handleLaunchTermination}
                className={TextButtonClassNames.Standard}
                afterIcon={<ArrowDropdown className="account__terminate__arrow" />}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
